/**
 * Main hook for managing form validation state
 */

import { useState, useCallback } from 'react'
import type { 
  FormValidationConfig, 
  ValidationState, 
  FormValidationHookResult, 
  ValidationResult 
} from '../types'
import { VALIDATORS } from '../registry'

export const useFormValidation = (config: FormValidationConfig): FormValidationHookResult => {
  const [validationState, setValidationState] = useState<ValidationState>({})

  // Validate a single field
  const validateField = useCallback((fieldName: string, value: string): ValidationResult => {
    const fieldConfig = config[fieldName]
    if (!fieldConfig) {
      return { isValid: true }
    }

    // Check required first
    if (fieldConfig.required) {
      const requiredValidator = VALIDATORS.required
      const requiredResult = requiredValidator.validate(value)
      if (!requiredResult.isValid) {
        return requiredResult
      }
    }

    // Skip other validators if empty and not required
    if (!value.trim() && !fieldConfig.required) {
      return { isValid: true }
    }

    // Run through all validators
    const validators = fieldConfig.validators || []
    for (const validatorConfig of validators) {
      const validator = VALIDATORS[validatorConfig.name]
      if (validator) {
        const result = validator.validate(value, validatorConfig.options)
        if (!result.isValid) {
          return {
            isValid: false,
            message: validatorConfig.customMessage || result.message
          }
        }
      }
    }

    return { isValid: true }
  }, [config])

  // Validate all fields in the form
  const validateAllFields = useCallback((formData: Record<string, any>): boolean => {
    const newValidationState: ValidationState = {}
    let hasErrors = false

    Object.keys(config).forEach(fieldName => {
      const value = formData[fieldName] || ''
      const result = validateField(fieldName, value)
      
      newValidationState[fieldName] = {
        isValid: result.isValid,
        message: result.message,
        isValidated: true,
        isDirty: true
      }
      
      if (!result.isValid) {
        hasErrors = true
      }
    })

    setValidationState(newValidationState)
    return !hasErrors
  }, [config, validateField])

  // Format input using field's validators
  const formatInput = useCallback((fieldName: string, value: string): string => {
    const fieldConfig = config[fieldName]
    if (!fieldConfig?.validators || !fieldConfig.formatOnInput) {
      return value
    }

    // Find the first validator that has formatInput function
    for (const validatorConfig of fieldConfig.validators) {
      const validator = VALIDATORS[validatorConfig.name]
      if (validator?.formatInput) {
        return validator.formatInput(value)
      }
    }

    return value
  }, [config])

  // Check if form has any validation errors
  const hasErrors = Object.values(validationState).some(
    state => state.isValidated && !state.isValid
  )

  // Check if specific fields have validation errors
  const hasValidationErrors = useCallback((fieldNames?: string[]): boolean => {
    const fieldsToCheck = fieldNames || Object.keys(validationState)
    return fieldsToCheck.some(fieldName => {
      const state = validationState[fieldName]
      return state?.isValidated && !state.isValid
    })
  }, [validationState])

  // Clear validation for a specific field
  const clearFieldValidation = useCallback((fieldName: string) => {
    setValidationState(prev => ({
      ...prev,
      [fieldName]: {
        isValid: true,
        message: undefined,
        isValidated: false,
        isDirty: false
      }
    }))
  }, [])

  // Clear all validation state
  const clearAllValidation = useCallback(() => {
    setValidationState({})
  }, [])

  // Set validation state for a specific field
  const setFieldValidation = useCallback((
    fieldName: string, 
    validation: Partial<ValidationState[string]>
  ) => {
    setValidationState(prev => ({
      ...prev,
      [fieldName]: {
        isValid: true,
        message: undefined,
        isValidated: false,
        isDirty: false,
        ...prev[fieldName],
        ...validation
      }
    }))
  }, [])

  return {
    validationState,
    validateField,
    validateAllFields,
    formatInput,
    hasErrors,
    hasValidationErrors,
    clearFieldValidation,
    clearAllValidation,
    setFieldValidation
  }
}
